// Validation utility functions and rules

export interface ValidationRule {
  rule: (value: any, formData?: any) => boolean;
  message: string;
}

export interface FieldValidation {
  [key: string]: ValidationRule[];
}

export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

// Common validation rules
export const validationRules = {
  required: (message = 'This field is required'): ValidationRule => ({
    rule: (value) => {
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'string') return value.trim().length > 0;
      return value !== null && value !== undefined && value !== '';
    },
    message,
  }),

  email: (message = 'Please enter a valid email address'): ValidationRule => ({
    rule: (value) => {
      if (!value) return true; // Allow empty if not required
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(value);
    },
    message,
  }),

  phone: (message = 'Please enter a valid phone number'): ValidationRule => ({
    rule: (value) => {
      if (!value) return true; // Allow empty if not required
      // Kuwait phone number format: +965 XXXX XXXX or similar
      const phoneRegex = /^(\+965\s?)?\d{4}\s?\d{4}$/;
      return phoneRegex.test(value.replace(/\s/g, ''));
    },
    message,
  }),

  minLength: (min: number, message?: string): ValidationRule => ({
    rule: (value) => {
      if (!value) return true; // Allow empty if not required
      return value.length >= min;
    },
    message: message || `Must be at least ${min} characters long`,
  }),

  maxLength: (max: number, message?: string): ValidationRule => ({
    rule: (value) => {
      if (!value) return true; // Allow empty if not required
      return value.length <= max;
    },
    message: message || `Must be no more than ${max} characters long`,
  }),

  min: (min: number, message?: string): ValidationRule => ({
    rule: (value) => {
      if (!value && value !== 0) return true; // Allow empty if not required
      return Number(value) >= min;
    },
    message: message || `Must be at least ${min}`,
  }),

  max: (max: number, message?: string): ValidationRule => ({
    rule: (value) => {
      if (!value && value !== 0) return true; // Allow empty if not required
      return Number(value) <= max;
    },
    message: message || `Must be no more than ${max}`,
  }),

  pattern: (regex: RegExp, message: string): ValidationRule => ({
    rule: (value) => {
      if (!value) return true; // Allow empty if not required
      return regex.test(value);
    },
    message,
  }),

  vin: (message = 'Please enter a valid VIN (17 characters)'): ValidationRule => ({
    rule: (value) => {
      if (!value) return true; // Allow empty if not required
      // VIN should be 17 characters, alphanumeric, excluding I, O, Q
      const vinRegex = /^[A-HJ-NPR-Z0-9]{17}$/;
      return vinRegex.test(value.toUpperCase());
    },
    message,
  }),

  plateNumber: (message = 'Please enter a valid plate number'): ValidationRule => ({
    rule: (value) => {
      if (!value) return true; // Allow empty if not required
      // Kuwait plate format: 3 letters followed by 3 numbers, or similar variations
      const plateRegex = /^[A-Z]{1,3}\s?\d{1,4}$|^\d{1,4}\s?[A-Z]{1,3}$/;
      return plateRegex.test(value.toUpperCase());
    },
    message,
  }),

  year: (message = 'Please enter a valid year'): ValidationRule => ({
    rule: (value) => {
      if (!value) return true; // Allow empty if not required
      const year = Number(value);
      const currentYear = new Date().getFullYear();
      return year >= 1900 && year <= currentYear + 1;
    },
    message,
  }),

  password: (message = 'Password must be at least 8 characters with uppercase, lowercase, number and special character'): ValidationRule => ({
    rule: (value) => {
      if (!value) return true; // Allow empty if not required
      // At least 8 characters, 1 uppercase, 1 lowercase, 1 number, 1 special character
      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
      return passwordRegex.test(value);
    },
    message,
  }),

  confirmPassword: (message = 'Passwords do not match'): ValidationRule => ({
    rule: (value, formData) => {
      if (!value) return true; // Allow empty if not required
      return value === formData?.password;
    },
    message,
  }),

  url: (message = 'Please enter a valid URL'): ValidationRule => ({
    rule: (value) => {
      if (!value) return true; // Allow empty if not required
      try {
        new URL(value);
        return true;
      } catch {
        return false;
      }
    },
    message,
  }),

  dateRange: (startField: string, message = 'End date must be after start date'): ValidationRule => ({
    rule: (value, formData) => {
      if (!value || !formData?.[startField]) return true;
      return new Date(value) >= new Date(formData[startField]);
    },
    message,
  }),

  uniqueInArray: (arrayField: string, message = 'Value must be unique'): ValidationRule => ({
    rule: (value, formData) => {
      if (!value || !formData?.[arrayField]) return true;
      const array = formData[arrayField];
      const occurrences = array.filter((item: any) => item === value).length;
      return occurrences <= 1;
    },
    message,
  }),

  fileSize: (maxSizeMB: number, message?: string): ValidationRule => ({
    rule: (file) => {
      if (!file) return true; // Allow empty if not required
      const maxSizeBytes = maxSizeMB * 1024 * 1024;
      return file.size <= maxSizeBytes;
    },
    message: message || `File size must be less than ${maxSizeMB}MB`,
  }),

  fileType: (allowedTypes: string[], message?: string): ValidationRule => ({
    rule: (file) => {
      if (!file) return true; // Allow empty if not required
      return allowedTypes.includes(file.type);
    },
    message: message || `File type must be one of: ${allowedTypes.join(', ')}`,
  }),
};

// Validation function
export const validateForm = (
  formData: any,
  validationSchema: FieldValidation
): ValidationResult => {
  const errors: Record<string, string> = {};
  let isValid = true;

  Object.keys(validationSchema).forEach((fieldName) => {
    const fieldRules = validationSchema[fieldName];
    const fieldValue = formData[fieldName];

    for (const rule of fieldRules) {
      if (!rule.rule(fieldValue, formData)) {
        errors[fieldName] = rule.message;
        isValid = false;
        break; // Stop at first validation error for this field
      }
    }
  });

  return { isValid, errors };
};

// Validation schema definitions for common forms
export const validationSchemas = {
  customer: {
    first_name: [
      validationRules.required('First name is required'),
      validationRules.minLength(2, 'First name must be at least 2 characters'),
      validationRules.maxLength(50, 'First name must be less than 50 characters'),
    ],
    last_name: [
      validationRules.required('Last name is required'),
      validationRules.minLength(2, 'Last name must be at least 2 characters'),
      validationRules.maxLength(50, 'Last name must be less than 50 characters'),
    ],
    email: [
      validationRules.email(), // Email is now optional
    ],
    phone: [
      validationRules.required('Phone number is required'),
      validationRules.phone(),
    ],
    address: [
      validationRules.maxLength(500, 'Address must be less than 500 characters'),
    ],
  },

  vehicle: {
    customer_id: [
      validationRules.required('Please select a customer'),
    ],
    make: [
      validationRules.required('Vehicle make is required'),
      validationRules.minLength(2, 'Make must be at least 2 characters'),
      validationRules.maxLength(50, 'Make must be less than 50 characters'),
    ],
    model: [
      validationRules.required('Vehicle model is required'),
      validationRules.minLength(1, 'Model must be at least 1 character'),
      validationRules.maxLength(50, 'Model must be less than 50 characters'),
    ],
    year: [
      validationRules.required('Year is required'),
      validationRules.year(),
    ],
    color: [
      validationRules.required('Color is required'),
      validationRules.minLength(3, 'Color must be at least 3 characters'),
      validationRules.maxLength(30, 'Color must be less than 30 characters'),
    ],
    plate_number: [
      validationRules.required('Plate number is required'),
      validationRules.plateNumber(),
    ],
    vin: [
      validationRules.vin(),
    ],
    engine_number: [
      validationRules.maxLength(50, 'Engine number must be less than 50 characters'),
    ],
    mileage: [
      validationRules.required('Mileage is required'),
      validationRules.min(0, 'Mileage cannot be negative'),
      validationRules.max(9999999, 'Mileage seems too high'),
    ],
    fuel_type: [
      validationRules.required('Fuel type is required'),
    ],
    transmission_type: [
      validationRules.required('Transmission type is required'),
    ],
    notes: [
      validationRules.maxLength(1000, 'Notes must be less than 1000 characters'),
    ],
  },

  jobCard: {
    customer_id: [
      validationRules.required('Please select a customer'),
    ],
    vehicle_id: [
      validationRules.required('Please select a vehicle'),
    ],
    description: [
      validationRules.required('Job description is required'),
      validationRules.minLength(10, 'Description must be at least 10 characters'),
      validationRules.maxLength(1000, 'Description must be less than 1000 characters'),
    ],
    estimated_cost: [
      validationRules.min(0, 'Cost cannot be negative'),
      validationRules.max(99999, 'Cost seems too high'),
    ],
    estimated_duration: [
      validationRules.min(0.1, 'Duration must be at least 0.1 hours'),
      validationRules.max(168, 'Duration cannot exceed 168 hours (1 week)'),
    ],
    priority: [
      validationRules.required('Priority is required'),
    ],
    scheduled_at: [
      validationRules.required('Scheduled date is required'),
    ],
  },

  invoice: {
    customer_id: [
      validationRules.required('Please select a customer'),
    ],
    job_card_id: [
      validationRules.required('Please select a job card'),
    ],
    subtotal: [
      validationRules.required('Subtotal is required'),
      validationRules.min(0, 'Subtotal cannot be negative'),
    ],
    tax_amount: [
      validationRules.min(0, 'Tax amount cannot be negative'),
    ],
    discount_amount: [
      validationRules.min(0, 'Discount amount cannot be negative'),
    ],
    total_amount: [
      validationRules.required('Total amount is required'),
      validationRules.min(0, 'Total amount cannot be negative'),
    ],
    due_date: [
      validationRules.required('Due date is required'),
      validationRules.dateRange('issue_date', 'Due date must be after issue date'),
    ],
  },

  user: {
    name: [
      validationRules.required('Full name is required'),
      validationRules.minLength(2, 'Name must be at least 2 characters'),
      validationRules.maxLength(100, 'Name must be less than 100 characters'),
    ],
    email: [
      validationRules.required('Email is required'),
      validationRules.email(),
    ],
    password: [
      validationRules.required('Password is required'),
      validationRules.password(),
    ],
    confirm_password: [
      validationRules.required('Please confirm your password'),
      validationRules.confirmPassword(),
    ],
    phone: [
      validationRules.phone(),
    ],
    role: [
      validationRules.required('Role is required'),
    ],
  },

  garage: {
    name: [
      validationRules.required('Garage name is required'),
      validationRules.minLength(3, 'Name must be at least 3 characters'),
      validationRules.maxLength(100, 'Name must be less than 100 characters'),
    ],
    email: [
      validationRules.required('Email is required'),
      validationRules.email(),
    ],
    phone: [
      validationRules.required('Phone number is required'),
      validationRules.phone(),
    ],
    address: [
      validationRules.required('Address is required'),
      validationRules.minLength(10, 'Address must be at least 10 characters'),
      validationRules.maxLength(500, 'Address must be less than 500 characters'),
    ],
    license_number: [
      validationRules.required('License number is required'),
      validationRules.minLength(5, 'License number must be at least 5 characters'),
    ],
  },

  payment: {
    amount: [
      validationRules.required('Payment amount is required'),
      validationRules.min(0.001, 'Amount must be greater than 0'),
      validationRules.max(99999, 'Amount seems too high'),
    ],
    payment_method: [
      validationRules.required('Payment method is required'),
    ],
    reference_number: [
      validationRules.minLength(3, 'Reference number must be at least 3 characters'),
    ],
  },

  inventory: {
    name: [
      validationRules.required('Item name is required'),
      validationRules.minLength(2, 'Name must be at least 2 characters'),
      validationRules.maxLength(100, 'Name must be less than 100 characters'),
    ],
    sku: [
      validationRules.required('SKU is required'),
      validationRules.minLength(3, 'SKU must be at least 3 characters'),
      validationRules.maxLength(50, 'SKU must be less than 50 characters'),
    ],
    category: [
      validationRules.required('Category is required'),
    ],
    quantity: [
      validationRules.required('Quantity is required'),
      validationRules.min(0, 'Quantity cannot be negative'),
    ],
    unit_price: [
      validationRules.required('Unit price is required'),
      validationRules.min(0, 'Price cannot be negative'),
    ],
    reorder_level: [
      validationRules.min(0, 'Reorder level cannot be negative'),
    ],
  },
};

// Async validation for unique values (would connect to backend)
export const asyncValidators = {
  uniqueEmail: async (email: string, excludeId?: number): Promise<boolean> => {
    // In production, this would make an API call
    // For now, return a mock validation
    return new Promise((resolve) => {
      setTimeout(() => {
        // Mock: emails ending with 'taken' are considered unavailable
        resolve(!email.toLowerCase().includes('taken'));
      }, 300);
    });
  },

  uniquePlateNumber: async (plateNumber: string, excludeId?: number): Promise<boolean> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        // Mock: plate 'TAKEN 123' is unavailable
        resolve(plateNumber.toUpperCase() !== 'TAKEN 123');
      }, 300);
    });
  },

  uniqueVin: async (vin: string, excludeId?: number): Promise<boolean> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        // Mock: VIN 'TAKEN123456789012' is unavailable
        resolve(vin.toUpperCase() !== 'TAKEN123456789012');
      }, 300);
    });
  },

  uniqueSku: async (sku: string, excludeId?: number): Promise<boolean> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        // Mock: SKU 'TAKEN' is unavailable
        resolve(sku.toUpperCase() !== 'TAKEN');
      }, 300);
    });
  },
};

// Real-time validation debounce helper
export const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Form field sanitization
export const sanitizers = {
  trim: (value: string) => value?.trim() || '',
  upperCase: (value: string) => value?.toUpperCase() || '',
  toLowerCase: (value: string) => value?.toLowerCase() || '',
  removeSpaces: (value: string) => value?.replace(/\s/g, '') || '',
  phoneNumber: (value: string) => {
    // Format Kuwait phone number
    const cleaned = value?.replace(/\D/g, '') || '';
    if (cleaned.startsWith('965')) {
      return `+965 ${cleaned.slice(3, 7)} ${cleaned.slice(7, 11)}`;
    }
    if (cleaned.length === 8) {
      return `+965 ${cleaned.slice(0, 4)} ${cleaned.slice(4)}`;
    }
    return value;
  },
  currency: (value: string) => {
    // Format currency input
    const num = parseFloat(value?.replace(/[^\d.-]/g, '') || '0');
    return isNaN(num) ? '0.000' : num.toFixed(3);
  },
  plateNumber: (value: string) => {
    // Format plate number (uppercase, proper spacing)
    return value?.toUpperCase().replace(/\s+/g, ' ').trim() || '';
  },
  vin: (value: string) => {
    // Format VIN (uppercase, no spaces)
    return value?.toUpperCase().replace(/\s/g, '') || '';
  },
};

export default {
  validationRules,
  validationSchemas,
  validateForm,
  asyncValidators,
  debounce,
  sanitizers,
};